import React from 'react';
import {Platform, StyleSheet} from 'react-native';

/**
 * Creates web-enhanced StyleSheet objects that work with React Native Web
 * Adds web-specific properties like cursor, hover states, and focus indicators
 * while maintaining compatibility with mobile platforms
 */
export const createWebEnhancedStyles = (styles: any) => {
  // If not on web, return regular styles
  if (Platform.OS !== 'web') {
    return StyleSheet.create(styles);
  }

  const enhancedStyles: any = {};

  Object.keys(styles).forEach((key: string) => {
    const style = styles[key];

    if (!style) {
      enhancedStyles[key] = style;
      return;
    }

    // Clone the original style
    enhancedStyles[key] = {...style};

    // Add web-specific enhancements based on component type
    // Interactive elements get cursor pointer
    if (
      key.toLowerCase().includes('button') ||
      key.toLowerCase().includes('pressable') ||
      key.toLowerCase().includes('touchable') ||
      key.toLowerCase().includes('link')
    ) {
      enhancedStyles[key] = {
        ...enhancedStyles[key],
        cursor: 'pointer',
      };
    }

    // Form inputs get better web styling
    if (
      key.toLowerCase().includes('input') ||
      key.toLowerCase().includes('textinput')
    ) {
      enhancedStyles[key] = {
        ...enhancedStyles[key],
        cursor: 'text',
      };
    }
  });

  return StyleSheet.create(enhancedStyles);
};

/**
 * Hook for web-specific interactive states
 * Provides hover and focus state management for web components
 */
export const useWebInteractionState = () => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isPressed, setIsPressed] = React.useState(false);

  const interactionProps =
    Platform.OS === 'web'
      ? {
          onHoverIn: () => setIsHovered(true),
          onHoverOut: () => setIsHovered(false),
          onFocus: () => setIsFocused(true),
          onBlur: () => setIsFocused(false),
        }
      : {};

  return {
    isHovered,
    isFocused,
    isPressed,
    setIsPressed,
    interactionProps,
  };
};

/**
 * Creates web-enhanced styles with hover and focus states
 * Automatically adds web-specific interactive styles when on web platform
 */
export const createInteractiveStyles = (
  baseStyles: any,
  interactiveStyles?: any,
) => {
  if (Platform.OS !== 'web' || !interactiveStyles) {
    return StyleSheet.create(baseStyles);
  }

  const enhancedStyles = {...baseStyles};

  // Add interactive state styles
  Object.keys(interactiveStyles).forEach((key: string) => {
    const interactiveStyle = interactiveStyles[key];
    if (interactiveStyle && baseStyles[key]) {
      enhancedStyles[key] = {...baseStyles[key]};
    }
  });

  return StyleSheet.create(enhancedStyles);
};

/**
 * Web-specific style helpers
 */
export const webStyles = {
  // Cursor styles
  cursor: {
    pointer: {cursor: 'pointer'},
    text: {cursor: 'text'},
    notAllowed: {cursor: 'not-allowed'},
  },

  // Focus styles (applied automatically by RNW for focus-visible)
  focusRing: {
    outline: '2px solid #7C6AB1',
    outlineOffset: 2,
  },

  // Web-specific layout helpers
  webOnly: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
};
